import React from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../../../../store/authStore'

/** The "Superusers only" page: a refused account, or a `403` from any console route. */
export const SuperusersOnlyState: React.FC = () => {
  const intl = useIntl()
  return (
    <div className='d-flex flex-column align-items-center justify-content-center gap-3 py-15 text-center' data-testid='superusers-only'>
      <h1 className='m-0'>{intl.formatMessage({ id: 'PLATFORM.gate.title' })}</h1>
      <p className='text-muted mb-0' style={{ maxWidth: 520 }}>
        {intl.formatMessage({ id: 'PLATFORM.gate.description' })}
      </p>
      <Link to='/' className='btn btn-light-primary'>
        {intl.formatMessage({ id: 'PLATFORM.gate.back' })}
      </Link>
    </div>
  )
}

const GateSkeleton: React.FC = () => (
  <div className='d-flex flex-column gap-5 skeleton' data-testid='gate-skeleton'>
    <div className='card' style={{ height: 127 }} />
    <div className='card' style={{ height: 480 }} />
  </div>
)

/**
 * Wraps a console page. The backend answers 403 on every `/platform/` route to
 * anyone who is not a superuser, so this is a courtesy that spares them a failed
 * request: `is_admin` comes from the profile the app already holds. A profile
 * that has not arrived yet shows a skeleton, so an outage or a slow load is never
 * mistaken for a refusal.
 */
export const SuperuserGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const profile = useAuthStore((state) => state.userProfile)
  if (!profile) return <GateSkeleton />
  if (profile.is_admin !== true) return <SuperusersOnlyState />
  return <>{children}</>
}

/** The `/platform/*` catch-all: reached only when the console's own routes are not registered for this user. */
export const PlatformFallback: React.FC = () => {
  const profile = useAuthStore((state) => state.userProfile)
  return profile ? <SuperusersOnlyState /> : <GateSkeleton />
}
